import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';

import './LikeDrawer.css';
import placeholderProfile from '../../assets/placeholder-profile.png';
import LikeDrawerSkeleton from '../skeleton/LikeDrawerSkeleton';
import api from '../../api';
import goldTickIcon from '../../assets/goldtick-icon.png';

export default function LikeDrawer({ onClose, postId, initialLikes = [] }) {
  const [items, setItems] = useState(initialLikes || []);
  const [total, setTotal] = useState(0);
  const [nextOffset, setNextOffset] = useState(0);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exhausted, setExhausted] = useState(false);
  const [error, setError] = useState('');
  const sentinelRef = useRef(null);

  const pushedRef = useRef(false);
  const closingFromButtonRef = useRef(false);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const safeClose = useCallback(({ manual }) => {
    if (manual) closingFromButtonRef.current = true;

    try {
      onCloseRef.current?.();
    } catch {}

    if (manual && pushedRef.current && window.history.state?.bfDrawer === 'likes') {
      try {
        window.history.back();
      } catch {}
      pushedRef.current = false;
    }
  }, []);

  const handleBackdropClick = useCallback(
    (e) => {
      if (e.target !== e.currentTarget) return;
      safeClose({ manual: true });
    },
    [safeClose]
  );

  const stopDrawerPropagation = useCallback((e) => {
    e.stopPropagation();
  }, []);

  useEffect(() => {
    try {
      window.history.pushState({ bfDrawer: 'likes', postId }, '');
      pushedRef.current = true;
    } catch {}

    const body = document.body;
    const prevOverflow = body.style.overflow;

    body.style.overflow = 'hidden';
    body.classList.add('bf-like-drawer-open');

    const onPopState = () => {
      if (closingFromButtonRef.current) {
        closingFromButtonRef.current = false;
        return;
      }

      safeClose({ manual: false });
    };

    window.addEventListener('popstate', onPopState);

    return () => {
      body.style.overflow = prevOverflow;
      body.classList.remove('bf-like-drawer-open');
      window.removeEventListener('popstate', onPopState);

      if (pushedRef.current && window.history.state?.bfDrawer === 'likes') {
        try {
          window.history.back();
        } catch {}
        pushedRef.current = false;
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPage = useCallback(
    async (offset = 0) => {
      const { data } = await api.get(`/interactions/${postId}/likes`, {
        params: { limit: 20, offset },
      });

      return data;
    },
    [postId]
  );

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setLoadingInitial(true);

        const data = await fetchPage(0);

        if (cancelled) return;

        setItems(data.items || []);
        setTotal(data.total || 0);

        if (data.nextOffset == null) {
          setExhausted(true);
          setNextOffset(0);
        } else {
          setExhausted(false);
          setNextOffset(data.nextOffset);
        }
      } catch (e) {
        if (!cancelled) setError('Failed to load likes.');
      } finally {
        if (!cancelled) setLoadingInitial(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [fetchPage]);

  const loadMore = useCallback(async () => {
    if (loadingMore || exhausted) return;

    setLoadingMore(true);

    try {
      const data = await fetchPage(nextOffset);

      setItems((prev) => prev.concat(data.items || []));

      if (data.nextOffset == null) {
        setExhausted(true);
      } else {
        setNextOffset(data.nextOffset);
      }
    } catch (e) {
    } finally {
      setLoadingMore(false);
    }
  }, [fetchPage, nextOffset, loadingMore, exhausted]);

  useEffect(() => {
    if (!sentinelRef.current || exhausted || loadingInitial) return;

    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { rootMargin: '200px' }
    );

    io.observe(sentinelRef.current);

    return () => io.disconnect();
  }, [loadMore, exhausted, loadingInitial]);

  const isAdminUser = (u) => u?.isAdmin === true || !!u?.adminRole;
  const isAppUser = (u) => u?.isApp === true || u?.accountType === 'app';
  const isSpecialUser = (u) => isAdminUser(u) || isAppUser(u);

  const getSpecialTitle = (u) => {
    if (isAdminUser(u)) return u?.adminRole || 'Admin';
    if (isAppUser(u)) return 'App account';
    return '';
  };

  const drawerSizeClass =
    loadingInitial || items.length >= 6
      ? 'like-drawer-large'
      : items.length <= 2
      ? 'like-drawer-compact'
      : 'like-drawer-medium';


return createPortal(
  <div
    className="like-drawer-backdrop"
    onClick={handleBackdropClick}
  >

      <div
        className={`like-drawer-content ${drawerSizeClass}`}
        onClick={stopDrawerPropagation}
        onMouseDown={stopDrawerPropagation}
        onTouchStart={stopDrawerPropagation}
      >
        <div className="like-header">
          <span>Likes{total ? ` • ${total}` : ''}</span>

          <button className="like-close" onClick={() => safeClose({ manual: true })}>
            ✕
          </button>
        </div>

        <div className="like-scroll">
          {loadingInitial ? (
            <LikeDrawerSkeleton />
          ) : error ? (
            <p className="no-likes">{error}</p>
          ) : items.length === 0 ? (
            <p className="no-likes">No likes yet.</p>
          ) : (
            <>
              {items.map((u, i) => (
                <div key={`${u?.username || 'u'}-${i}`} className="like-entry">
                  <img
                    src={u?.profilePic?.startsWith('http') ? u.profilePic : placeholderProfile}
                    alt="avatar"
                    className="like-avatar"
                  />

                  <Link to={`/user/${u?.username}`} className="like-username">
                    <span className="like-username-row">
                      <span>{u?.username}</span>

                      {isSpecialUser(u) && (
                        <img
                          src={goldTickIcon}
                          alt={isAdminUser(u) ? 'Admin verified' : 'App verified'}
                          className="like-gold-tick"
                          title={getSpecialTitle(u)}
                        />
                      )}
                    </span>
                  </Link>
                </div>
              ))}

              {!exhausted && (
                <div ref={sentinelRef} className="like-loading-sentinel">
                  {loadingMore && (
                    <div className="like-loading-spinner" aria-label="Loading more" />
                  )}
                </div>
              )}

              {exhausted && items.length > 0 && (
                <div className="like-end-cap">You’ve reached the end</div>
              )}
            </>
          )}
        </div>
      </div>


  </div>,
  document.body
);
}
